<template>
  <div class="extra-bio">
    <header>
      <Avatar :picture="extra.getPicture()" size="165px" @click="openPicture" @on-img403="onImg403" />
      <span v-if="extra.isFavorite()" class="extra-bio-status"><QIcon name="favorite" /></span>
    </header>
    <div class="relative-position">
      <div v-if="extra.hasRefused() && displayOverlay" class="overlay">{{ $t("ETAB.PAGES.EXTRA.@REFUSED_EXTRA") }}</div>
      <article>
        <div class="text-center">
          <QChip v-if="extra.getTags().isNovice()" color="primary" text-color="white" :label="$t('ETAB.PAGES.CANDIDATE.CARD.@NOVICE')" />
          <QChip
            v-if="extra.getTags().isCollectiveChefCertified()"
            color="primary"
            text-color="white"
            :label="$t('ETAB.PAGES.CANDIDATE.CARD.@MAKI_COLLECTIVE_CHEF_CERTIFIED')"
          />
          <QChip
            v-if="extra.getTags().isCollectivePartyLeaderCeritified()"
            color="primary"
            text-color="white"
            :label="$t('ETAB.PAGES.CANDIDATE.CARD.@MAKI_COLLECTIVE_PARTY_LEADER_CERTIFIED')"
          />
          <QChip
            v-if="extra.getTags().isTEButlerCertified()"
            color="primary"
            text-color="white"
            :label="$t('ETAB.PAGES.CANDIDATE.CARD.@MAKI_TE_BUTLER_CERTIFIED')"
          />
        </div>
        <div class="extra-bio-identity">
          <h2>{{ identity.firstname }}</h2>
          <h2>{{ identity.lastname }}</h2>
        </div>
        <template v-if="candidateReports?.getTotalReports()">
          <QChip class="q-pr-none bg-grey-2">
            <QAvatar icon="fa fa-triangle-exclamation" color="red" text-color="white" />
            {{ $tc("ETAB.PAGES.CANDIDATE.CARD.@REPORTS", candidateReports.getTotalReports()) }}
            <QBtn color="grey" round flat dense :icon="expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'" @click="expanded = !expanded" />
          </QChip>

          <QSlideTransition>
            <div v-show="expanded">
              <QList class="bg-grey-2 rounded-borders q-mx-md" separator>
                <QItem
                  v-for="candidateReportKey in candidateReportsTabs"
                  v-show="candidateReports[CANDIDATE_REPORTS[candidateReportKey].code] > 0"
                  :key="candidateReportKey"
                  class="text-left"
                >
                  <QItemSection>
                    <QItemLabel class="text-caption">
                      <QIcon :name="CANDIDATE_REPORTS[candidateReportKey].icon" color="red" size="18px" class="q-mr-sm q-mb-xs align-bottom" />
                      {{ $t(CANDIDATE_REPORTS[candidateReportKey].text) }}
                    </QItemLabel>
                  </QItemSection>

                  <QItemSection side>
                    <QBadge color="red">
                      {{ candidateReports[CANDIDATE_REPORTS[candidateReportKey].code] }}
                    </QBadge>
                  </QItemSection>
                </QItem>
              </QList>
            </div>
          </QSlideTransition>
        </template>
        <section v-if="getOlympicsGamesSkills.length > 0" class="text-center extra-skills-card">
          <div class="extra-skills-card-title flex row bg-secondary">
            <QIcon name="fa-solid fa-circle-check" class="extra-skills-card-title-icon" />
            <p class="text-white q-ml-sm">{{ $t("ETAB.PAGES.CANDIDATE.CARD.@CANDIDATE_SKILLS") }}</p>
          </div>
          <div class="q-pt-md">
            <div v-for="(skill, index) in getOlympicsGamesSkills" :key="index">
              <p class="text-left extra-skills-card-label">{{ skill }}</p>
            </div>
          </div>
        </section>
        <section v-if="shouldDisplayQualifications">
          <ExtraRoundedTag
            v-for="(qualification, index) in extra.getQualificationsOnExperiences()"
            :key="qualification + '_' + index"
            :label="qualification"
            color="primary-light"
          />
        </section>
        <section v-if="shouldDisplayPresentation" class="extra-presentation">
          <span class="extra-comment-quotes">{{ extra.getPresentation() }}</span>
        </section>
      </article>
      <footer>
        <template v-if="shouldDisplayInfo">
          <p v-if="displayCity">
            <span>
              <QIcon name="fad fa-location-dot" color="secondary" size="21px" class="margin-0-5" />
              {{ displayCity }}
            </span>
          </p>
          <p>
            <span v-if="extra.getAge() > 0">
              <QIcon name="fad fa-cake-candles" color="secondary" size="21px" class="margin-0-5" />
              {{ $t("ETAB.PAGES.EXTRA.@AGE", { age: extra.getAge() }) }}
            </span>
            <span v-if="extra.isMotorized()">
              <QIcon name="fad fa-car" color="secondary" size="21px" class="margin-0-5" />
              {{ $t("ETAB.PAGES.EXTRA.@IS_MOTORIZED") }}
            </span>
          </p>
        </template>
        <template v-if="canAccessContactInformations">
          <p v-if="formattedPhoneNumber">
            <span>
              <QIcon name="fad fa-phone" color="secondary" size="21px" class="margin-0-5" />
              {{ formattedPhoneNumber }}
            </span>
          </p>
          <p v-if="identity.email">
            <span>
              <QIcon name="fad fa-envelope" color="secondary" size="21px" class="margin-0-5" />
              {{ identity.email }}
            </span>
          </p>
        </template>
      </footer>
    </div>

    <PhotoModal v-if="modalOpened" v-model:opened="modalOpened" :title="identity.firstname" :picture="extra.getPicture()" />
  </div>
</template>

<script>
  import { ExtraModel } from "@core/models";
  import { JavascriptHelpers } from "@core/helpers";
  import { CANDIDATE_REPORTS } from "@core/constants";
  import { GETTER_EXTRA_GET_SKILLS } from "@etab/stores/types";
  import { useExtraStore } from "@etab/stores/extra";
  import { mapState } from "pinia";
  import PhotoModal from "./Photo.modal.vue";

  export default {
    components: {
      PhotoModal,
    },
    props: {
      extra: {
        type: Object,
        default: () => new ExtraModel(),
      },
      lite: {
        type: Boolean,
        default: false,
      },
      canAccessContactInformations: {
        type: Boolean,
        default: false,
      },
      displayOverlay: {
        type: Boolean,
        default: true,
      },
    },
    data() {
      return {
        modalOpened: false,
        imgError: false,
        tab: null,
        expanded: false,
      };
    },
    computed: {
      candidateReports() {
        return this.extra.getCandidateReports();
      },
      candidateReportsTabs() {
        return Object.keys(CANDIDATE_REPORTS);
      },
      identity() {
        return this.extra.getIdentity();
      },
      formattedPhoneNumber() {
        return JavascriptHelpers.formatPhoneNumber(this.identity.phone);
      },
      shouldDisplayQualifications() {
        return !this.lite && this.extra.getQualificationsOnExperiences().length > 0;
      },
      shouldDisplayPresentation() {
        return !this.lite && this.extra.getPresentation().length > 0;
      },
      shouldDisplayInfo() {
        return !this.lite && (this.displayCity.length > 0 || this.extra.getAge() > 0 || this.extra.isMotorized());
      },
      displayCity() {
        const postalCode = this.identity.postalCode;
        const city = this.identity.city;
        if (postalCode && city) {
          return this.$t("ETAB.PAGES.EXTRA.@CODE_CITY", { postalCode, city });
        }
        if (postalCode && !city) {
          return postalCode;
        }
        if (!postalCode && city) {
          return city;
        }
        return "";
      },
      ...mapState(useExtraStore, {
        getOlympicsGamesSkills: GETTER_EXTRA_GET_SKILLS,
      }),
    },
    created() {
      this.tab = this.candidateReportsTabs[0];
      this.CANDIDATE_REPORTS = CANDIDATE_REPORTS;
    },
    methods: {
      openPicture() {
        if (!this.imgError && this.extra.getPicture()) {
          this.modalOpened = true;
        }
      },
      onImg403() {
        this.imgError = true;
      },
    },
  };
</script>

<style lang="scss">
  .extra-bio {
    margin-bottom: 20px;
    header {
      text-align: center;
      position: relative;
      min-height: 170px;
      margin-top: -25px; // image translate compensation
      z-index: 2;

      img {
        cursor: pointer;
        box-shadow: $img-shadow;
        transform: translate(0, 50px);
      }

      .extra-bio-status {
        position: absolute;
        top: 50px;
        left: 60%;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        width: 35px;
        height: 35px;
        text-align: center;
        font-size: 21px;
        background-color: white;
        color: $secondary;
      }
    }
    .overlay {
      position: absolute;
      top: 0;
      bottom: 0;
      width: 100%;
      background-color: rgba($primary, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      backdrop-filter: blur(1px);
      z-index: 1;
      color: white;
      font-weight: bold;
      font-size: 18px;
    }

    .extra-skills-card {
      color: $primary;
      font-size: 16px;
      box-shadow: $btn-shadow;
      border-radius: 10px;
      overflow: hidden;
      border: 1px solid $secondary;
      width: auto;
      margin: 16px;
      @media screen and (max-width: $breakpoint-sm-max) {
        margin: 16px 0px;
      }
      &-title {
        height: 40px;
        padding: 10px;
        &-icon {
          margin-top: 2px;
          color: #ffaeba;
        }
        > p {
          @media screen and (max-width: $breakpoint-sm-max) {
            font-size: 14px;
          }
        }
      }
      &-label {
        padding-left: 35px;
        @media screen and (max-width: $breakpoint-sm-max) {
          font-size: 14px;
        }
      }
    }

    article {
      background-color: #ffffff;
      border-top-left-radius: 4px;
      border-top-right-radius: 4px;
      box-shadow: $card-shadow;
      padding: 55px 5px 10px;
      text-align: center;

      > header {
        margin: 5px 0;

        h2 {
          margin: 0;
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: hidden;
        }
      }

      > section {
        margin: 15px 0 0;

        &.extra-presentation {
          padding: 0 5px;
          font-weight: 600;
        }
      }
    }

    footer {
      border-bottom-left-radius: 4px;
      border-bottom-right-radius: 4px;
      background-color: $superlightgrey;
      padding: 5px 10px;
      box-shadow: $card-shadow;

      > p {
        margin: 10px 0;
        display: flex;
        align-items: center;
        justify-content: left;

        > span {
          margin: 0 10px;
        }
      }
    }
  }
</style>
